import * as React from "react";
import { Grid, AlertColor } from "@mui/material";
import { DataGridPremium, getGridSingleSelectOperators } from "@mui/x-data-grid-premium";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import { useGetGlDataQuery, useDeleteGlGroupMutation } from "../../../services/cclTokenedPayrollApi";
import { GlGroup } from "../../../services/types/payrollEventTypes";
import AddGlGroupDrawer from "./addGlGroupDrawer";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

function getDateFromTimestamp(date: any) {
    if (!date) {
        return "";
    }
    return new Date(date).toLocaleDateString();
}

interface GlGroupsPanelProps {}

const GlGroupsPanel: React.FC<GlGroupsPanelProps> = () => {
    const [addGroupDrawerOpen, setAddGroupDrawerOpen] = React.useState<boolean>(false);
    const [drawerMode, setDrawerMode] = React.useState<"add" | "edit">("add");
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [selectedGroup, setSelectedGroup] = React.useState<GlGroup | null>(null);

    // Snackbar state
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const { data: glData, isLoading: loading } = useGetGlDataQuery();
    const [deleteGlGroup] = useDeleteGlGroupMutation();
    const { logEvent } = useLogAccessEvent();

    const sortedGlGroups = React.useMemo(() => {
        return [...(glData?.glGroups ?? [])].sort((a, b) =>
            String(a.groupCode ?? "").localeCompare(String(b.groupCode ?? ""))
        );
    }, [glData?.glGroups]);

    const isExactlyOneSelected = selectedRows.length === 1;

    const currentSelectedGroup = React.useMemo(() => {
        if (!isExactlyOneSelected) {
            return null;
        }
        return (glData?.glGroups ?? []).find(
            (g) => g.groupCode === String(selectedRows[0])
        ) ?? null;
    }, [isExactlyOneSelected, selectedRows, glData?.glGroups]);

    const deleteButtonCaption = React.useMemo(() => {
        return currentSelectedGroup?.active ? "Disable GL Group" : "Enable GL Group";
    }, [currentSelectedGroup?.active]);

    const handleToolbarButtonClick = (btnId: string) => {
        switch (btnId) {
            case "add-group-button":
                setDrawerMode("add");
                setSelectedGroup(null);
                setAddGroupDrawerOpen(true);
                return;
            case "edit-group-button": {
                if (!isExactlyOneSelected) {
                    return;
                }

                const group = (glData?.glGroups ?? []).find(
                    (g) => g.groupCode === String(selectedRows[0])
                );

                if (!group) {
                    return;
                }

                setDrawerMode("edit");
                setSelectedGroup(group);
                setAddGroupDrawerOpen(true);
                return;
            }
            case "delete-group-button": {
                if (!isExactlyOneSelected || !currentSelectedGroup) {
                    return;
                }

                deleteGlGroup(currentSelectedGroup.groupCode)
                    .unwrap()
                    .then(() => {
                        logEvent(currentSelectedGroup.active ? "GLGroupDisabled" : "GLGroupEnabled", null);
                        setSelectedRows([]);
                        
                        // Show success snackbar
                        const message = currentSelectedGroup.active ? "GL Group Disabled " : "GL Group Enabled";
                        setSnackbarMessage(message);
                        setSnackbarSeverity("success");
                        setShowSnackbar(true);
                    })
                    .catch((error) => {
                        console.error("Error toggling GL group:", error);
                        
                        // Show error snackbar
                        setSnackbarMessage("Failed to toggle GL Group");
                        setSnackbarSeverity("error");
                        setShowSnackbar(true);
                    });
                return;
            }
            default:
                return;
        }
    };

    const COLUMNS = [
        {
            field: "groupCode",
            headerName: "Code",
            width: 200,
        },
        {
            field: "groupDesc",
            headerName: "Description",
            width: 200,
        },
        {
            field: "active",
            headerName: "Enabled",
            type: "singleSelect",
            valueOptions: [
                { value: true, label: "Yes" },
                { value: false, label: "No" },
            ],
            filterOperators: [...getGridSingleSelectOperators().filter((op) => op.value === "is")],
            width: 100,
            renderCell: (params: any) => {
                return params.value ? "Yes" : "No";
            }
        },
                {
            field: "createdBy",
            headerName: "Created By",
            width: 200,
        },
        {
            field: "created",
            headerName: "Created",
            valueGetter: (params: any) => getDateFromTimestamp(params.row.created),
            width: 200,
        },
        {
            field: "modifiedBy",
            headerName: "Modified By",
            width: 200,
        },
        {
            field: "modified",
            headerName: "Modified",
            valueGetter: (params: any) => getDateFromTimestamp(params.row.modified),
            width: 200,
        }
    ];

    return (
        <React.Fragment>
            <AddGlGroupDrawer
                open={addGroupDrawerOpen}
                mode={drawerMode}
                group={selectedGroup}
                onClose={() => setAddGroupDrawerOpen(false)}
                onSaveResult={(message, severity) => {
                    setSnackbarMessage(message);
                    setSnackbarSeverity(severity);
                    setShowSnackbar(true);
                }}
            />
            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />
            <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={{ justify: "space-between", flexGrow: 1 }}
            >
                <Grid
                    item
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: 1,
                        height: "calc(100vh - 220px)",
                        minHeight: 400,
                    }}
                >
                    <DataGridPremium 
                        columns={COLUMNS}
                        components={{Toolbar: CclDataGridToolbar}}
                        getRowId={(row) => row.groupCode}
                        componentsProps={{
                            toolbar: {
                                showSaveState: false,
                                hideSettingsButtons: true,
                                savestate: null,
                                clearstate: null,
                                anySelected: isExactlyOneSelected,
                                miscButtons: [
                                    {
                                        id: "add-group-button",
                                        caption: "Add GL Group",
                                        onClick: null,
                                        returnSelected: false,
                                        restricted: false,
                                    },
                                    {
                                        id: "edit-group-button",
                                        caption: "Edit GL Group",
                                        onClick: null,
                                        returnSelected: false,
                                        restricted: false,
                                        disable: !isExactlyOneSelected,
                                    },
                                    {
                                        id: "delete-group-button",
                                        caption: deleteButtonCaption,
                                        onClick: null,
                                        returnSelected: false,
                                        restricted: false,
                                        disable: !isExactlyOneSelected,
                                    }
                                ],
                                btnClick: handleToolbarButtonClick,
                            },
                        }}
                        checkboxSelection
                        disableSelectionOnClick
                        onSelectionModelChange={(newSelection: any) =>
                            setSelectedRows(newSelection as Array<string | number>)
                        }
                        rows={sortedGlGroups}
                        loading={loading}

                    />
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default GlGroupsPanel;




